import React from 'react';
import { useTranslation } from 'react-i18next';
import type { InvoicePaymentController } from '@/features/Profile/sections/Packages/hooks';
import { TerminalPaymentTargetType } from '@/api/types/payments.types';
import { WarningModal } from '@/components/Ad/WarningModal';
import { InfoDrawer } from '@/components/Ad/Promotions/InfoDrawer';
import { AddBalance } from '@/components/Payment/AddBalance';
import { PaymentInTerminals } from '@/components/Payment/PaymentInTerminals';
import { Button } from '@/components/UI/Button/Button';

type InvoicePaymentDialogsProps = {
  controller: InvoicePaymentController;
  packageName: string;
};

export function InvoicePaymentDialogs({
  controller,
  packageName,
}: InvoicePaymentDialogsProps) {
  const { t } = useTranslation();
  const {
    clearCreatePaymentError,
    closeTooManyRequests,
    createPaymentError,
    handleBalanceTopUpSuccess,
    isAddBalanceOpen,
    isAppleGooglePayEnabled,
    isDuplicate,
    isNotEnoughFundsOpen,
    isTerminalOpen,
    isTooManyRequestsOpen,
    paymentMethods,
    startAddBalance,
    terminalInvoice,
    toggleAddBalance,
    toggleNotEnoughFunds,
    toggleTerminal,
  } = controller;

  return (
    <>
      <WarningModal
        isOpen={isNotEnoughFundsOpen}
        onClose={toggleNotEnoughFunds}
        title={t('common.attention')}
        description={t('pages.ad.not_enough_money_top_up_your_balance')}
        actions={
          <>
            <Button
              appearance="secondaryOutlined"
              borderRadius={14}
              isFullWidth
              onClick={toggleNotEnoughFunds}
            >
              {t('common.close')}
            </Button>
            <Button
              appearance="secondary"
              borderRadius={14}
              isFullWidth
              onClick={startAddBalance}
            >
              {t('common.top_up')}
            </Button>
          </>
        }
      />

      <InfoDrawer
        isOpen={isTooManyRequestsOpen}
        onClose={closeTooManyRequests}
        description={t('pages.ad.too_many_requests')}
      />
      <InfoDrawer
        isOpen={Boolean(createPaymentError)}
        onClose={clearCreatePaymentError}
        description={createPaymentError}
        isError={!isDuplicate}
      />

      {isAddBalanceOpen && (
        <AddBalance
          isOpen
          toggle={toggleAddBalance}
          isAppleGooglePayEnabled={isAppleGooglePayEnabled}
          paymentMethods={paymentMethods}
          onBalanceTopUpSuccess={handleBalanceTopUpSuccess}
        />
      )}

      {isTerminalOpen && (
        <PaymentInTerminals
          isOpen
          toggle={toggleTerminal}
          paymentTargetType={TerminalPaymentTargetType.Invoice}
          invoice={
            terminalInvoice
              ? {
                  amount: terminalInvoice.amount,
                  legacyResourceId: terminalInvoice.legacyResourceId,
                  serviceName: packageName,
                }
              : undefined
          }
        />
      )}
    </>
  );
}
